import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  useToast,
} from '@adop/ui';
import { Archive, ArchiveRestore, Pencil, Plus, Store } from 'lucide-react';
import { useWorkspace } from '../../lib/workspace-context';
import { useAuth } from '../../lib/auth-context';
import { useAsync } from '../../lib/useAsync';
import {
  insertLocation,
  listLocations,
  setLocationArchived,
  updateLocation,
  type LocationRow,
} from '../../lib/data';
import { errorMessage } from '../../lib/api';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { SlugInput } from '../../components/SlugInput';
import { ListSkeleton } from '../../components/LoadingState';

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

export function LocationsPage() {
  const { restaurant, canOwn, location: activeLocation, setLocationId } = useWorkspace();
  const { refresh } = useAuth();
  const { toast } = useToast();
  const state = useAsync(
    () => (restaurant ? listLocations(restaurant.id) : Promise.resolve([])),
    [restaurant?.id],
  );

  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState(browserTimezone());
  const [renameTarget, setRenameTarget] = useState<LocationRow | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [archiveTarget, setArchiveTarget] = useState<{ location: LocationRow; archive: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!restaurant) {
    return <EmptyState icon={<Store className="h-6 w-6" />} title="No restaurant selected" />;
  }

  const addLocation = async () => {
    setBusy(true);
    setError(null);
    try {
      await insertLocation({
        restaurantId: restaurant.id,
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        timezone,
        address: city.trim() ? { city: city.trim(), country: 'US' } : undefined,
        phone: phone.trim() || undefined,
      });
      toast({ title: 'Location added', variant: 'success' });
      setAddOpen(false);
      setName('');
      setSlug('');
      setCity('');
      setPhone('');
      state.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async () => {
    if (!renameTarget) return;
    try {
      await updateLocation(renameTarget.id, { name: renameValue.trim() || renameTarget.name });
      toast({ title: 'Location renamed', variant: 'success' });
      setRenameTarget(null);
      state.reload();
      void refresh();
    } catch (cause) {
      toast({ title: 'Could not rename', description: errorMessage(cause), variant: 'error' });
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    try {
      await setLocationArchived(archiveTarget.location.id, archiveTarget.archive);
      toast({ title: archiveTarget.archive ? 'Location archived' : 'Location restored', variant: 'success' });
      setArchiveTarget(null);
      state.reload();
      void refresh();
    } catch (cause) {
      toast({ title: 'Could not update', description: errorMessage(cause), variant: 'error' });
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Locations"
        description="Manage each restaurant location."
        action={
          canOwn ? (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" /> Add location
            </Button>
          ) : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {state.loading ? (
        <ListSkeleton rows={2} />
      ) : (state.data ?? []).length === 0 ? (
        <EmptyState icon={<Store className="h-6 w-6" />} title="No locations" />
      ) : (
        <div className="space-y-3">
          {(state.data ?? []).map((location) => (
            <Card key={location.id}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <CardTitle>{location.name}</CardTitle>
                    {location.id === activeLocation?.id ? <Badge variant="info">Active</Badge> : null}
                    {location.archived_at ? <Badge variant="default">Archived</Badge> : null}
                    {location.published ? <Badge variant="success">Published</Badge> : null}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {location.id !== activeLocation?.id && !location.archived_at ? (
                      <Button variant="outline" size="sm" onClick={() => setLocationId(location.id)}>
                        Switch
                      </Button>
                    ) : null}
                    {canOwn ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRenameTarget(location);
                            setRenameValue(location.name);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setArchiveTarget({ location, archive: !location.archived_at })}
                        >
                          {location.archived_at ? (
                            <ArchiveRestore className="h-4 w-4" />
                          ) : (
                            <Archive className="h-4 w-4 text-amber-700" />
                          )}
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-500">
                  {location.slug}.adop.app · {location.timezone}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add location"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button loading={busy} disabled={!name.trim() || slug.trim().length < 3} onClick={() => void addLocation()}>
              Add location
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Name" value={name} onChange={(event) => setName(event.target.value)} />
          <div>
            <p className="mb-1 text-sm font-medium text-zinc-900">Web address</p>
            <SlugInput value={slug} onChange={setSlug} />
          </div>
          <Input label="City" value={city} onChange={(event) => setCity(event.target.value)} />
          <Input label="Phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
          <Input label="Timezone" value={timezone} onChange={(event) => setTimezone(event.target.value)} />
        </div>
      </Modal>

      <Modal
        open={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        title="Rename location"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button onClick={() => void saveRename()}>Save</Button>
          </>
        }
      >
        <Input label="Name" value={renameValue} onChange={(event) => setRenameValue(event.target.value)} />
      </Modal>

      <ConfirmDialog
        open={archiveTarget !== null}
        title={archiveTarget?.archive ? 'Archive this location?' : 'Restore this location?'}
        message={
          archiveTarget?.archive
            ? 'It will stop appearing on the storefront and marketplace.'
            : 'It will become available again.'
        }
        confirmLabel={archiveTarget?.archive ? 'Archive' : 'Restore'}
        variant={archiveTarget?.archive ? 'danger' : 'primary'}
        onConfirm={() => void confirmArchive()}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
}
